import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  OPEN_PREFERENCES_EVENT,
  readConsent,
  saveConsent,
} from "@/lib/consent";

const TEXT =
  "Sua privacidade é importante para nós. Utilizamos cookies necessários para o funcionamento do site e, com sua autorização, cookies de análise e marketing para melhorar sua experiência e nossas campanhas.";

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const saved = readConsent();
    if (!saved) {
      setVisible(true);
    } else {
      setAnalytics(saved.analytics);
      setMarketing(saved.marketing);
    }

    const open = () => {
      const current = readConsent();
      if (current) {
        setAnalytics(current.analytics);
        setMarketing(current.marketing);
      }
      setPanelOpen(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, open);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open);
  }, []);

  const acceptAll = () => {
    saveConsent({ decision: "accepted", analytics: true, marketing: true });
    setAnalytics(true);
    setMarketing(true);
    setVisible(false);
    setPanelOpen(false);
  };

  const rejectAll = () => {
    saveConsent({ decision: "rejected", analytics: false, marketing: false });
    setAnalytics(false);
    setMarketing(false);
    setVisible(false);
    setPanelOpen(false);
  };

  const saveCustom = () => {
    const all = analytics && marketing;
    const none = !analytics && !marketing;
    saveConsent({
      decision: all ? "accepted" : none ? "rejected" : "custom",
      analytics,
      marketing,
    });
    setVisible(false);
    setPanelOpen(false);
  };

  return (
    <>
      {visible && (
        <aside
          role="dialog"
          aria-live="polite"
          aria-label="Aviso de cookies"
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-background/98 backdrop-blur supports-[backdrop-filter]:bg-background/90 shadow-2xl"
        >
          <div className="container mx-auto flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between md:py-5">
            <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">{TEXT}</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap md:shrink-0">
              <Button onClick={acceptAll} className="font-semibold uppercase tracking-wide">
                Aceitar todos
              </Button>
              <Button variant="outline" onClick={rejectAll} className="uppercase tracking-wide">
                Recusar
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPanelOpen(true)}
                className="uppercase tracking-wide"
              >
                Configurar
              </Button>
            </div>
          </div>
        </aside>
      )}

      <Dialog open={panelOpen} onOpenChange={setPanelOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Preferências de cookies</DialogTitle>
            <DialogDescription>
              Escolha quais categorias de cookies você autoriza. Você pode alterar essa
              decisão a qualquer momento no rodapé do site.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div>
                <p className="font-medium">Necessários</p>
                <p className="text-sm text-muted-foreground">
                  Essenciais para navegação, segurança e envio de formulários. Sempre ativos.
                </p>
              </div>
              <Switch checked disabled aria-label="Cookies necessários (sempre ativos)" />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div>
                <p className="font-medium">Análise</p>
                <p className="text-sm text-muted-foreground">
                  Google Analytics 4 — entender páginas visitadas e melhorar o site.
                </p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                aria-label="Cookies de análise"
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div>
                <p className="font-medium">Marketing</p>
                <p className="text-sm text-muted-foreground">
                  Google Ads e pixels de anúncios — medir conversões das campanhas.
                </p>
              </div>
              <Switch
                checked={marketing}
                onCheckedChange={setMarketing}
                aria-label="Cookies de marketing"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" onClick={rejectAll} className="uppercase tracking-wide">
              Recusar todos
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={saveCustom}>
                Salvar preferências
              </Button>
              <Button onClick={acceptAll}>Aceitar todos</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
